/* ═══════════════════════════════════════════════════════════════════════════
   Agencijski nalog — tura koju vozi EKSTERNI prevoznik.

   Stoji kao zaseban tab PRE Putnog naloga i vodi podatke o podizvođaču:
   ko vozi, kojim vozilom, po kojoj ceni i pod kojim rokom plaćanja.

   ── ZAŠTO JE ODVOJEN OD PUTNOG NALOGA ──────────────────────────────────────
   Putni nalog ostaje nepromenjen i izgleda isto kao na Turama — on vodi NAŠE
   vozilo, vozača, kilometražu i gorivo. Agencijski nalog ne menja ništa od
   toga nego dodaje sloj prevoznika. Oba pišu u isti red `trip.PutniNalog`,
   razlikuje ih `jeAgencijski`, pa tura ostaje jedna i ima jednu blagajnu,
   jedne dnevnice i jedan obračun.

   ── ŠTA SE NE VODI, I ZAŠTO ────────────────────────────────────────────────
   „Firma" sa starog ekrana ovde ne postoji: TMS je višefirmski preko tokena,
   pa se firma ne bira nego se zna iz JWT-a.

   „Relacija" i „Cena prevozniku (EUR)" su sa slike precrtane, pa ih forma ne
   prikazuje. Relacija se i inače samo izvodila iz radnih naloga — procedura
   `trip.IzmeniPutniNalog` je nikad nije ni upisivala, tako da uklanjanje polja
   ništa ne gubi. Kolona `cenaPrevoznikuEur` ostaje u bazi (nova je i prazna),
   ali je forma ne popunjava.

   „Metod raspodele" je pojam iz CYCLE-a koji TMS nema — ako zatreba, ide kao
   šifarnik u modgen, ne kao slobodan tekst.
   ═══════════════════════════════════════════════════════════════════════════ */

import React, { useState, useEffect, useRef, useContext } from 'react';
import './Ture.css';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import CustomDropdown from '../../custom/customDropdown';
import { AuthContext } from '../../Header/auth - context';
import { uzmiGresku } from '../../custom/greske';

/* Datum na backend uvek kroz ovo: `toISOString()` bez korekcije pomeri dan
   unazad za vremenske zone istočno od UTC (30.04. 00:00 -> 29.04. 22:00). */
const toUtcIso = (d) => {
    if (!d) return null;
    const x = d instanceof Date ? d : new Date(d);
    const utc = new Date(x.getTime() - x.getTimezoneOffset() * 60000);
    return utc.toISOString().slice(0, 19) + 'Z';
};

/* Prazan broj nije 0 nego NULL — 0 bi se upisala kao stvarna cena. */
const uBroj = (v) => {
    if (v === '' || v === null || v === undefined) return null;
    const n = Number(String(v).replace(',', '.'));
    return Number.isFinite(n) ? n : null;
};

const PRAZNO = {
    brojNaloga: '',
    prevoznik: null,
    voziloPrevoznika: '',
    vozacPrevoznika: '',
    brojPasosaPrevoznika: '',
    telefonPrevoznika: '',
    napomenaPutniNalog: '',
    datumPolaska: null,
    datumDolaska: null,
    cenaPrevozniku: '',
    valutaPrevoznika: null,
    rokPlacanjaPrevozniku: '',
    tipKamiona: null,
    statusTure: null,
    odgovornoLicePrevoznika: '',
};

const AgencijskiNalog = () => {
    const { t } = useTranslation();
    const auth = useContext(AuthContext);
    const toast = useRef(null);

    /* Koji je nalog izabran — isti izvor koji koristi i Putni nalog, pa traka
       za izbor iznad tabova upravlja i ovim ekranom. */
    const trip = useSelector(state => state.trip);

    const [podaci, setPodaci] = useState(PRAZNO);
    const [prevozniciOptions, setPrevozniciOptions] = useState([]);
    const [valuteOptions, setValuteOptions] = useState([]);
    const [tipKamionaOptions, setTipKamionaOptions] = useState([]);
    const [statusTureOptions, setStatusTureOptions] = useState([]);
    const [snima, setSnima] = useState(false);
    const [poslato, setPoslato] = useState(false);

    const zaglavlja = () => ({
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + auth.token,
    });

    const naGresku = (e) => uzmiGresku(e, t('ture.greskaAgencijski')).then(m =>
        toast.current?.show({ severity: 'error', summary: t('common.greska'), detail: m.message, life: 5000 }));

    /* ---- šifarnici ---- */
    useEffect(() => {
        fetch(process.env.REACT_APP_BACKEND_URL + '/trip', {
            method: 'POST', headers: zaglavlja(), body: JSON.stringify({ id: trip ?? null }),
        })
            .then(r => (r.ok ? r.json() : Promise.reject(r)))
            .then(data => {
                const p = JSON.parse(data[0]['']);
                setPrevozniciOptions(p.PrevozniciDB || []);
                setValuteOptions(p.ValuteOptions || []);
                setTipKamionaOptions(p.TipKamionaOptions || []);
                setStatusTureOptions(p.StatusTure || []);
            })
            .catch(naGresku);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trip]);

    /* ---- učitavanje izabranog naloga ---- */
    useEffect(() => {
        if (!trip) { setPodaci(PRAZNO); setPoslato(false); return; }

        fetch(process.env.REACT_APP_BACKEND_URL + '/trip/FillInTripData', {
            method: 'POST', headers: zaglavlja(), body: JSON.stringify({ id: trip }),
        })
            .then(r => (r.ok ? r.json() : Promise.reject(r)))
            .then(d => {
                const n = Array.isArray(d) ? d[0] : d;
                if (!n) return;
                setPodaci({
                    brojNaloga: n.ID || '',
                    prevoznik: n.prevoznik ?? null,
                    voziloPrevoznika: n.voziloPrevoznika || '',
                    vozacPrevoznika: n.vozacPrevoznika || '',
                    brojPasosaPrevoznika: n.brojPasosaPrevoznika || '',
                    telefonPrevoznika: n.telefonPrevoznika || '',
                    napomenaPutniNalog: n.napomenaPutniNalog || '',
                    datumPolaska: n.datumPolaska ? new Date(n.datumPolaska) : null,
                    datumDolaska: n.datumDolaska ? new Date(n.datumDolaska) : null,
                    cenaPrevozniku: n.cenaPrevozniku ?? '',
                    valutaPrevoznika: n.valutaPrevoznika ?? null,
                    rokPlacanjaPrevozniku: n.rokPlacanjaPrevozniku ?? '',
                    tipKamiona: n.tipKamiona ?? null,
                    statusTure: n.statusTure ?? null,
                    odgovornoLicePrevoznika: n.odgovornoLicePrevoznika || '',
                });
            })
            .catch(naGresku);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trip]);

    const promeni = (e) => {
        const { name, value } = e.target;
        setPodaci(prev => ({ ...prev, [name]: value }));
    };

    const nijePopunjeno = (v) => poslato && (v === null || v === undefined || v === '');

    const sacuvaj = () => {
        setPoslato(true);

        /* Prevoznik i datum polaska su jedini obavezni: bez prevoznika nalog
           nije agencijski, bez datuma ne pripada nijednom periodu. */
        if (!podaci.prevoznik || !podaci.datumPolaska) {
            toast.current?.show({
                severity: 'warn', summary: t('common.upozorenje'),
                detail: t('ture.agencijskiObavezna'), life: 4000,
            });
            return;
        }

        const telo = {
            ...podaci,
            jeAgencijski: 1,
            /* Vozilo, vozač, kilometraža i gorivo se NE šalju — njih vodi tab
               Putni nalog. `samoPrevoznik` govori proceduri da ih ZADRŽI: bez
               toga bi ih upisala kao NULL (jer ih u JSON-u nema) i tiho obrisala
               ono što je uneto na drugom tabu. Provereno — tako se i ponašalo. */
            samoPrevoznik: 1,
            /* `statusTure` je u bazi NOT NULL — prazan dropdown bi obio INSERT
               sa „Cannot insert the value NULL". Zato se uzima PRVA stavka
               šifarnika („Kreirana"), a ne zakucan broj: šifre su `IDENTITY` po
               bazi i razlikuju se od baze do baze. Broj ostaje samo kao poslednja
               odbrana ako spisak nije stigao. */
            statusTure: podaci.statusTure != null
                ? podaci.statusTure : (statusTureOptions?.[0]?.value ?? 144),
            /* Isto i za `obracunTure` (NOT NULL): 147 = „Neobracunata". Pri izmeni
               ga procedura ignorise jer je u zasticenoj listi `samoPrevoznik` —
               obracun se vodi na tabu Obracun ture, ne odavde. */
            obracunTure: 147,
            datumPolaska: toUtcIso(podaci.datumPolaska),
            datumDolaska: toUtcIso(podaci.datumDolaska),
            cenaPrevozniku: uBroj(podaci.cenaPrevozniku),
            rokPlacanjaPrevozniku: uBroj(podaci.rokPlacanjaPrevozniku),
            ostalaVozila: [],
            ostaliVozaci: [],
        };

        const novi = !trip;
        setSnima(true);
        fetch(process.env.REACT_APP_BACKEND_URL + (novi ? '/trip/InsertTrip' : '/trip/UpdateTrip'), {
            method: 'POST', headers: zaglavlja(),
            body: JSON.stringify(novi
                ? { formDataTripLog: telo }
                : { formDataTripLog: { ...telo, id: trip }, id: trip }),
        })
            .then(r => (r.ok ? r.json() : Promise.reject(r)))
            .then(d => {
                toast.current?.show({
                    severity: 'success', summary: t('common.uspeh'),
                    detail: d?.message || t('ture.agencijskiSacuvan'), life: 3000,
                });
            })
            .catch(naGresku)
            .finally(() => setSnima(false));
    };

    /* Tri kolone — raspored prati stari CYCLE ekran: levo prevoznik i njegovi
       kontakti, u sredini broj, datumi i cena, desno rok, tip kamiona i status.

       Kolone NISU `md:col-4`: `.cardTripLog .p-card-content` je flex sa
       `space-between`, a `.gridPutniNalog` je širok 30% (Ture.css). Kolona je,
       dakle, jedan `gridPutniNalog` div — isti obrazac koji koristi i Putni
       nalog. Sva polja u JEDNOM takvom divu su se stiskala u 30% širine
       ekrana, i to je bio razlog što forma nije ličila na sliku. */
    return (
        <div className="p-fluid grid formUnosZap">
            <Toast ref={toast} />

            <div className="col-12 md:col-6">
                <Card className="cardTripLog">

                    {/* ── 1. kolona: broj, prevoznik i njegovi podaci ─────── */}
                    <div className="gridPutniNalog">
                        <div className="col-12">
                            <label htmlFor="brojNaloga">{t('ture.brojAgencijskog')}:</label>
                            {/* Broj dodeljuje sopstveni brojač pri snimanju (sufiks AN),
                                pa se ne unosi nego prikazuje. */}
                            <InputText id="brojNaloga" name="brojNaloga" disabled
                                       className="poljeIzracunato" value={podaci.brojNaloga} />
                        </div>
                        <div className="col-12">
                            <label htmlFor="prevoznik">{t('ture.prevoznik')}:</label>
                            <CustomDropdown
                                id="prevoznik" name="prevoznik" filter
                                invalid={nijePopunjeno(podaci.prevoznik)}
                                value={podaci.prevoznik} options={prevozniciOptions}
                                onChange={promeni} style={{ width: '100%' }}
                            />
                        </div>
                        <div className="col-12">
                            <label htmlFor="voziloPrevoznika">{t('ture.voziloPrevoznika')}:</label>
                            <InputText id="voziloPrevoznika" name="voziloPrevoznika"
                                       value={podaci.voziloPrevoznika} onChange={promeni} />
                        </div>
                        <div className="col-12">
                            <label htmlFor="vozacPrevoznika">{t('ture.vozacPrevoznika')}:</label>
                            <InputText id="vozacPrevoznika" name="vozacPrevoznika"
                                       value={podaci.vozacPrevoznika} onChange={promeni} />
                        </div>
                        <div className="col-12">
                            <label htmlFor="brojPasosaPrevoznika">{t('ture.brojPasosa')}:</label>
                            <InputText id="brojPasosaPrevoznika" name="brojPasosaPrevoznika"
                                       value={podaci.brojPasosaPrevoznika} onChange={promeni} />
                        </div>
                        <div className="col-12">
                            <label htmlFor="telefonPrevoznika">{t('ture.telefonPrevoznika')}:</label>
                            <InputText id="telefonPrevoznika" name="telefonPrevoznika"
                                       value={podaci.telefonPrevoznika} onChange={promeni} />
                        </div>
                        <div className="col-12">
                            <label htmlFor="napomenaPutniNalog">{t('nova.napomena')}:</label>
                            <InputTextarea id="napomenaPutniNalog" name="napomenaPutniNalog" rows={4}
                                           value={podaci.napomenaPutniNalog} onChange={promeni} />
                        </div>
                    </div>

                    {/* ── 2. kolona: broj, datumi, cena ───────────────────── */}
                    <div className="gridPutniNalog">
                        <div className="col-12">
                            <label htmlFor="datumPolaska">{t('ture.datumPolaska')}:</label>
                            <Calendar id="datumPolaska" name="datumPolaska" mask="99.99.9999 99:99"
                                      invalid={nijePopunjeno(podaci.datumPolaska)}
                                      value={podaci.datumPolaska} onChange={promeni}
                                      showIcon dateFormat="dd.mm.yy" showTime hourFormat="24" />
                        </div>
                        <div className="col-12">
                            <label htmlFor="datumDolaska">{t('ture.datumDolaska')}:</label>
                            <Calendar id="datumDolaska" name="datumDolaska" mask="99.99.9999 99:99"
                                      value={podaci.datumDolaska} onChange={promeni}
                                      showIcon dateFormat="dd.mm.yy" showTime hourFormat="24" />
                        </div>
                        <div className="col-12">
                            <label htmlFor="cenaPrevozniku">{t('ture.cenaPrevozniku')}:</label>
                            <InputText id="cenaPrevozniku" name="cenaPrevozniku" keyfilter="num"
                                       value={podaci.cenaPrevozniku} onChange={promeni} />
                        </div>
                        <div className="col-12">
                            <label htmlFor="valutaPrevoznika">{t('nova.valuta')}:</label>
                            <Dropdown id="valutaPrevoznika" name="valutaPrevoznika" showClear
                                      value={podaci.valutaPrevoznika} options={valuteOptions}
                                      onChange={promeni} placeholder={t('nova.valuta')} />
                        </div>
                    </div>

                    {/* ── 3. kolona: uslovi prevoza ───────────────────────── */}
                    <div className="gridPutniNalog">
                        <div className="col-12">
                            <label htmlFor="rokPlacanjaPrevozniku">{t('ture.rokPlacanjaPrevozniku')}:</label>
                            <InputText id="rokPlacanjaPrevozniku" name="rokPlacanjaPrevozniku" keyfilter="int"
                                       value={podaci.rokPlacanjaPrevozniku} onChange={promeni} />
                        </div>
                        <div className="col-12">
                            <label htmlFor="tipKamiona">{t('ture.tipKamiona')}:</label>
                            <Dropdown id="tipKamiona" name="tipKamiona" showClear
                                      value={podaci.tipKamiona} options={tipKamionaOptions}
                                      onChange={promeni} placeholder={t('ture.tipKamiona')} />
                        </div>
                        <div className="col-12">
                            <label htmlFor="statusTure">{t('ture.statusTure')}:</label>
                            <Dropdown id="statusTure" name="statusTure" showClear
                                      value={podaci.statusTure} options={statusTureOptions}
                                      onChange={promeni} placeholder={t('ture.statusTure')} />
                        </div>
                        <div className="col-12">
                            <label htmlFor="odgovornoLicePrevoznika">{t('ture.odgovornoLice')}:</label>
                            <InputText id="odgovornoLicePrevoznika" name="odgovornoLicePrevoznika"
                                       value={podaci.odgovornoLicePrevoznika} onChange={promeni} />
                        </div>
                    </div>

                </Card>
            </div>

            <div className="formUnosZap buttonUnosZap">
                <button type="button" onClick={sacuvaj} disabled={snima}
                        style={{ minWidth: '20%' }} className="btn btn-success">
                    {trip ? t('common.save') : t('common.unos')}
                </button>
            </div>
        </div>
    );
};

export default AgencijskiNalog;
